import type { TableCellProperties } from "@kaleidoscope-solutions/engine-wasm"
import {
  CellDisplayKind,
  CellKind,
  ExpandCollapse,
  Gap,
  SortDirection,
} from "@kaleidoscope-solutions/engine-wasm"

// Parsed from a WASM JSON grid string into a pure Javascript dense model representing the grid.
// Iterating the grid will yield zero or more Rows, iterating a Row will yield zero or more Cells.
export class Grid implements Iterable<Row> {
  private columnCount!: number

  private id!: string[]

  // Cell spanning
  private colspan!: number[]
  private rowspan!: number[]
  private hiddenBySpan!: boolean[]

  // Display
  private displayKind!: string[]
  private readOnly!: boolean[]
  private formattedValue!: string[]
  private valueType!: string[]
  private cellKind!: string[]
  private adjacentCellGaps!: string[][]
  private emptyRelationship!: boolean[]
  private expandCollapse!: string[]

  // Sort
  private sortable!: boolean[]
  private sortDirection!: string[]

  // Position
  private firstCellInRow!: boolean[]
  private lastCellInRow!: boolean[]
  private firstRow!: boolean[]
  private lastRow!: boolean[]
  private hierarchyLevel!: number[]
  private nextRowNested!: boolean[]

  // Set max rows and columns for performance reasons
  // TODO #489 - temp fix to remove
  // https://github.com/kaleidoscope-solutions/ks-ui-web/issues/489
  public maxRows: number | undefined = 100
  public maxCols: number | undefined = 50

  get rowCount(): number {
    if (!this.id || this.id.length === 0) {
      return 0
    }

    const rowCount = Math.ceil(this.id.length / this.columnCount)

    if (this.maxRows === undefined) {
      return rowCount
    }

    return Math.min(this.maxRows, rowCount)
  }

  get rowsTruncated() {
    if (this.maxRows === undefined) {
      return false
    }
    const rowCount = Math.ceil(this.id.length / this.columnCount)
    return rowCount >= this.maxRows
  }

  get colCount(): number {
    if (this.maxCols === undefined) {
      return this.columnCount
    }
    return Math.min(this.maxCols, this.columnCount)
  }

  get colsTruncated() {
    if (this.maxCols === undefined) {
      return false
    }
    return this.columnCount >= this.maxCols
  }

  index(row: number, column: number) {
    return row * this.columnCount + column
  }

  cellId(row: number, column: number): string {
    return this.id[this.index(row, column)]
  }

  cellColspan(row: number, column: number): number {
    const colSpan = this.colspan[this.index(row, column)]

    // Return the maxCols for colSpan as to not break the grid layout
    // TODO: Remove this when we remove maxCols
    if (this.maxCols !== undefined) {
      return Math.min(colSpan, this.maxCols - column)
    }

    return colSpan
  }

  cellRowspan(row: number, column: number): number {
    const rowSpan = this.rowspan[this.index(row, column)]

    // Return the maxRows for rowSpan as to not break the grid layout
    // TODO: Remove this when we remove maxRows
    if (this.maxRows !== undefined) {
      return Math.min(rowSpan, this.maxRows - row)
    }

    return rowSpan
  }

  cellDisplayKind(row: number, column: number): CellDisplayKind {
    const cellDisplayKind = this.displayKind[this.index(row, column)]
    return CellDisplayKind[cellDisplayKind as any] as unknown as CellDisplayKind
  }

  cellHiddenBySpan(row: number, column: number): boolean {
    return this.hiddenBySpan[this.index(row, column)]
  }

  cellReadOnly(row: number, column: number): boolean {
    return this.readOnly[this.index(row, column)]
  }

  cellFormattedValue(row: number, column: number): string {
    return this.formattedValue[this.index(row, column)]
  }

  cellValueType(row: number, column: number): string | undefined {
    const valueType = this.valueType[this.index(row, column)]
    return valueType == null ? undefined : valueType
  }

  cellCellKind(row: number, column: number): CellKind | undefined {
    const cellKind = this.cellKind[this.index(row, column)]
    return cellKind == null
      ? undefined
      : (CellKind[cellKind as any] as unknown as CellKind)
  }

  cellAdjacentCellGaps(row: number, column: number): Gap[] {
    const gaps =
      this.adjacentCellGaps[this.index(row, column)] ?? ([] as unknown[])
    return gaps.map((key: any) => Gap[key as any] as unknown) as Gap[]
  }

  cellFirstCellInRow(row: number, column: number): boolean {
    return this.firstCellInRow[this.index(row, column)]
  }

  cellLastCellInRow(row: number, column: number): boolean {
    return this.lastCellInRow[this.index(row, column)]
  }

  cellFirstRow(row: number, column: number): boolean {
    return this.firstRow[this.index(row, column)]
  }

  cellLastRow(row: number, column: number): boolean {
    return this.lastRow[this.index(row, column)]
  }

  cellHierarchyLevel(row: number, column: number): number {
    return this.hierarchyLevel[this.index(row, column)]
  }

  cellNextRowNested(row: number, column: number): boolean {
    return this.nextRowNested[this.index(row, column)]
  }

  cellEmptyRelationship(row: number, column: number): boolean {
    return this.emptyRelationship[this.index(row, column)]
  }

  cellSortable(row: number, column: number): boolean {
    return this.sortable[this.index(row, column)]
  }

  cellSortDirection(row: number, column: number): SortDirection {
    const sortDirection = this.sortDirection[this.index(row, column)]
    return SortDirection[sortDirection as any] as unknown as SortDirection
  }

  cellExpandCollapse(row: number, column: number): ExpandCollapse {
    const expandCollapse = this.expandCollapse[this.index(row, column)]
    return ExpandCollapse[expandCollapse as any] as unknown as ExpandCollapse
  }

  // Find last row index with a display kind of CellDisplayKind.Cell
  // We don't want to find Summary or Gaps.
  findLastCellRowIndex(): number {
    for (let row = this.rowCount; row--; row >= 0) {
      const cell = new Cell(row, 0, this)
      if (cell.displayKind === CellDisplayKind.Cell) {
        return row
      }
    }
    return -1
  }

  [Symbol.iterator]() {
    let rowCounter = 0
    const row = new Row(rowCounter, this)
    return {
      next: () => {
        if (rowCounter < this.rowCount) {
          row.row = rowCounter
        }
        rowCounter++

        return {
          done: rowCounter > this.rowCount,
          value: row,
        }
      },
    }
  }
}

// This should not be instantiated directly.
export class Row implements Iterable<Cell> {
  private _row: number
  private _grid: Grid

  constructor(row: number, grid: Grid) {
    validateRow(row, grid)
    this._row = row
    this._grid = grid
  }

  get row(): number {
    return this._row
  }

  set row(row: number) {
    validateRow(row, this._grid)
    this._row = row
  }

  [Symbol.iterator]() {
    let columnCounter = 0

    return {
      next: () => {
        const cell =
          columnCounter < this._grid.colCount
            ? new Cell(this._row, columnCounter, this._grid)
            : new Cell(0, 0, this._grid)
        columnCounter++

        return {
          done: columnCounter > this._grid.colCount,
          value: cell,
        }
      },
    }
  }
}

// Properties pulled from TableCellProperties
type TableCellPropertiesKeys = {
  [key in keyof TableCellProperties]: unknown
}

export interface GridCell extends TableCellPropertiesKeys {
  id: string // panel, row, col eg. Body-4-15
  readOnly: boolean
  displayKind: CellDisplayKind
  valueType: string | undefined
  formattedValue: string // value
  emptyRelationship: boolean
  expandCollapse: ExpandCollapse

  // Sort
  sortable: boolean
  sortDirection: SortDirection

  // Spans
  colspan: number
  rowspan: number
  hiddenBySpan: boolean

  cellKind: CellKind | undefined
  adjacentCellGaps: Gap[]
  hierarchyLevel: number
  nextRowNested: boolean

  // Position
  firstCellInRow: boolean
  lastCellInRow: boolean
  firstRow: boolean
  lastRow: boolean
}

export class Cell implements GridCell {
  private _row: number
  private _column: number
  private _grid: Grid

  constructor(row: number, column: number, grid: Grid) {
    validateCoordinates(row, column, grid)
    this._row = row
    this._column = column
    this._grid = grid
  }

  setPosition(row: number, column: number) {
    validateCoordinates(row, column, this._grid)
    this._row = row
    this._column = column
  }

  get columnCount(): number {
    return this._grid.colCount
  }

  get row(): number {
    return this._row
  }

  get column(): number {
    return this._column
  }

  get id(): string {
    return this._grid.cellId(this._row, this._column)
  }

  get colspan(): number {
    return this._grid.cellColspan(this._row, this._column)
  }

  get rowspan(): number {
    return this._grid.cellRowspan(this._row, this._column)
  }

  get displayKind(): CellDisplayKind {
    return this._grid.cellDisplayKind(this._row, this._column)
  }

  get hiddenBySpan(): boolean {
    return this._grid.cellHiddenBySpan(this._row, this._column)
  }

  get readOnly(): boolean {
    return this._grid.cellReadOnly(this._row, this._column)
  }

  get formattedValue(): string {
    return this._grid.cellFormattedValue(this._row, this._column)
  }

  get valueType(): string | undefined {
    return this._grid.cellValueType(this._row, this._column)
  }

  get cellKind(): CellKind | undefined {
    return this._grid.cellCellKind(this._row, this._column)
  }

  get adjacentCellGaps(): Gap[] {
    return this._grid.cellAdjacentCellGaps(this._row, this._column)
  }

  get firstCellInRow(): boolean {
    return this._grid.cellFirstCellInRow(this._row, this._column)
  }

  get lastCellInRow(): boolean {
    return this._grid.cellLastCellInRow(this._row, this._column)
  }

  get firstRow(): boolean {
    return this._grid.cellFirstRow(this._row, this._column)
  }

  get lastRow(): boolean {
    return this._grid.cellLastRow(this._row, this._column)
  }

  get hierarchyLevel(): number {
    return this._grid.cellHierarchyLevel(this._row, this._column)
  }

  get nextRowNested(): boolean {
    return this._grid.cellNextRowNested(this._row, this._column)
  }

  get emptyRelationship(): boolean {
    return this._grid.cellEmptyRelationship(this._row, this._column)
  }

  get sortable(): boolean {
    return this._grid.cellSortable(this._row, this._column)
  }

  get sortDirection(): SortDirection {
    return this._grid.cellSortDirection(this._row, this._column)
  }

  get expandCollapse(): ExpandCollapse {
    return this._grid.cellExpandCollapse(this._row, this._column)
  }
}

export function gridFromCellProperties(
  cellProperties: TableCellProperties
): Grid {
  return Object.assign(new Grid(), cellProperties)
}

function validateCoordinates(row: number, column: number, grid: Grid) {
  validateRow(row, grid)
  validateColumn(column, grid)
}

function validateRow(row: number, grid: Grid) {
  if (row >= grid.rowCount) {
    throw new Error(
      `row ${row} is outside of the grid bounds (${grid.rowCount})`
    )
  }
}

function validateColumn(column: number, grid: Grid) {
  if (column >= grid.colCount) {
    throw new Error(
      `column ${column} is outside of the grid bounds (${grid.colCount})`
    )
  }
}
